import React from "react";
import {
  EmptyState,
  EmptyStateProps,
  LoadPhase,
  RowActionsMenu,
  SkeletonCard,
  useEndReached,
} from "../../../components/ui";
import {
  DECLINED_STYLE,
  RowNewTag,
  STATUS_STYLES,
  isAwaitingAnswer,
  tintFor,
} from "./EnquiriesTable";
// The same tick and cross the table answers a request with — the same act on
// the same record, so the same control. See `PaymentAnswer`.
import PaymentAnswer from "../../payments/components/PaymentAnswer";
import { formatPrice } from "../../brand/planFormat";
import type { GateProps } from "../../billing/hooks/useSubscriptionGate";
import type { Enquiry } from "../types/enquiries.types";

/** The height the cards stand in at while the list is on its way — about four
 *  cards, which is what a phone shows before the fold. */
const CARDS_SKELETON = "h-[380px]";

interface EnquiryCardsProps {
  enquiries: Enquiry[];
  /** Stands in for the cards when the list comes back with none. */
  empty: EmptyStateProps;
  /** Where the list is — the same contract `EnquiriesTable` works to. */
  phase?: LoadPhase;
  onSelectEnquiry: (enquiry: Enquiry) => void;
  /** Opens the Change status dialog on this card. */
  onChangeStatus: (enquiry: Enquiry) => void;
  /** What Change status wears while the workspace has no plan behind it. */
  statusGate: GateProps;
  /** What the two answers wear while the workspace has no plan behind it. */
  answerGate: GateProps;
  /** The card whose request is being fetched, if any — its tick spins for the
   *  moment between the press and the question. */
  answering?: string | null;
  onConfirmPayment: (enquiry: Enquiry) => void;
  onDeclinePayment: (enquiry: Enquiry) => void;
  /** Whether the server holds pages past the ones handed over. */
  hasMore?: boolean;
  /** Whether one of those is on the wire — see `EnquiriesState`. */
  loadingMore?: boolean;
  /** Asks for the next page — see `useEndReached`. */
  onEndReached?: () => void;
}

/**
 * The enquiries as they read on a phone: one card per enquiry instead of a
 * table, and no card around the lot of them.
 *
 * The table needs 860px to lay its six columns out. On a phone that is a card
 * scrolled sideways inside a page, and the two columns that matter most here —
 * where the enquiry stands, and the pair of answers an outstanding request is
 * waiting on — are the last ones off the right-hand edge. So the row becomes a
 * card carrying what somebody working a pipeline is after: who asked, what for,
 * how long ago, and what there is to do about it.
 *
 * Every column is on the face of the card, so there is no sheet behind it.
 * Pressing the card opens the enquiry, which is what the table's rows do too —
 * and the two answers and the menu stop that press, since they act on the card
 * rather than follow it.
 *
 * Which of the two renders is `AllEnquiriesPage`'s decision — see `useIsMobile`.
 */
const EnquiryCards: React.FC<EnquiryCardsProps> = ({
  enquiries,
  empty,
  phase = "ready",
  onSelectEnquiry,
  onChangeStatus,
  statusGate,
  answerGate,
  answering = null,
  onConfirmPayment,
  onDeclinePayment,
  hasMore = false,
  loadingMore = false,
  onEndReached,
}) => {
  const { ref, onScroll } = useEndReached<HTMLDivElement>(hasMore, onEndReached);

  if (phase !== "ready" && enquiries.length === 0) {
    return (
      <div className="flex-1 min-h-0">
        <SkeletonCard phase={phase} className={CARDS_SKELETON} />
      </div>
    );
  }

  if (enquiries.length === 0) {
    // A column rather than a row, so the state gets the full width to set its
    // lines in. It brings its own padding.
    return (
      <div className="flex-1 min-h-0 flex flex-col justify-center">
        <EmptyState {...empty} />
      </div>
    );
  }

  return (
    <div
      ref={ref}
      onScroll={onScroll}
      className="flex-1 min-h-0 overflow-y-auto custom-scrollbar-overlay"
    >
      <ul className="flex flex-col gap-3 pb-4">
        {enquiries.map((enquiry) => {
          const awaiting = isAwaitingAnswer(enquiry);
          // Named in the answers' labels where the card knows it, since an icon
          // has no words of its own and the amount is what makes it clear which
          // request is being settled.
          const asked = enquiry.payment
            ? ` ${formatPrice(enquiry.payment.amount)}`
            : "";
          // Only where the stage is still the one the request put it at — see
          // `DECLINED_STYLE`.
          const declined =
            enquiry.status === "Payment sent" &&
            enquiry.payment?.status === "Cancelled";

          return (
            <li key={enquiry.id}>
              {/* The whole card is pressable for a thumb, and the name
                  inside it is a real button so the list stays reachable by
                  keyboard — the arrangement `EnquiriesTable` gives its rows,
                  and for the reason it has to be: this card also holds the
                  actions menu and, on an outstanding request, two more
                  buttons. A control inside a control is markup no browser
                  agrees on, so the card carries no role of its own.

                  `active:` rather than `hover:` — a phone has no pointer to
                  hover with, and the press is what wants acknowledging. */}
              <div
                onClick={() => onSelectEnquiry(enquiry)}
                className="w-full cursor-pointer card-surface border border-line bg-white p-4 text-left transition-colors active:bg-[#F8FEFF]"
              >
                <div className="flex items-start gap-3">
                  <span
                    className={`w-10 h-10 flex-shrink-0 rounded-full flex items-center justify-center ${tintFor(
                      enquiry.name
                    )}`}
                  >
                    <span className="text-[15px] font-medium">
                      {enquiry.name.charAt(0)}
                    </span>
                  </span>

                  {/* `min-w-0` is what lets a long name shorten instead of
                      pushing the menu off the card's edge. */}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="min-w-0 truncate text-[15px] font-medium text-ink">
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            onSelectEnquiry(enquiry);
                          }}
                          className="max-w-full truncate rounded text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
                        >
                          {enquiry.name}
                        </button>
                      </h3>
                      {/* Beside the name rather than down with the stage: it
                          says nothing about where the enquiry stands, only
                          that nobody here has read it yet. */}
                      <RowNewTag enquiry={enquiry} />
                    </div>
                    {/* Every goal on one line, cut off by the card's edge
                        rather than counted into a "+N" the way the table does
                        it: that pill opens its full list on hover, and there is
                        no hover here to open it on. */}
                    <p className="mt-0.5 truncate text-[13px] text-ink-muted">
                      {enquiry.goals.length > 0 ? enquiry.goals.join(", ") : "-"}
                    </p>
                  </div>

                  {/* The menu acts on the card rather than following it, so it
                      stops its own presses from opening the enquiry under it. */}
                  <div
                    className="flex-shrink-0"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <RowActionsMenu
                      label={`Actions for ${enquiry.name}`}
                      actions={[
                        {
                          label: "View details",
                          onSelect: () => onSelectEnquiry(enquiry),
                        },
                        // Greyed rather than dropped while the workspace has no
                        // plan behind it, so the press that comes anyway is
                        // answered: an entry that had quietly gone would read
                        // as this enquiry having no stage left to move to.
                        {
                          label: "Change status",
                          disabled: statusGate.disabled,
                          onSelect: statusGate.disabled
                            ? statusGate.onDisabledClick ?? (() => {})
                            : () => onChangeStatus(enquiry),
                        },
                      ]}
                    />
                  </div>
                </div>

                <div className="mt-3 flex items-center justify-between gap-3 border-t border-line pt-3">
                  <span className="min-w-0 truncate text-[12px] text-ink-subtle">
                    {enquiry.mode} · {enquiry.requestedOn} · {enquiry.daysAgo}
                  </span>

                  {/* A request still out is not a state to report so much as a
                      question to answer, so the corner is the two answers
                      rather than a pill saying the money was asked for — which
                      is already what the card is about. Every other stage has
                      nothing left to ask, and shows its pill. */}
                  {awaiting ? (
                    <div
                      className="flex flex-shrink-0 items-center gap-2"
                      onClick={(event) => event.stopPropagation()}
                    >
                      {/* Said for a screen reader, which cannot see that the
                          corner holds a tick and a cross rather than a word. */}
                      <span className="sr-only">Payment sent</span>
                      <PaymentAnswer
                        tone="confirm"
                        label={`Mark the${asked} payment requested from ${enquiry.name} as received`}
                        gate={answerGate}
                        busy={answering === enquiry.id}
                        onClick={() => onConfirmPayment(enquiry)}
                      />
                      <PaymentAnswer
                        tone="cancel"
                        label={`Decline the${asked} payment requested from ${enquiry.name}`}
                        gate={answerGate}
                        busy={answering === enquiry.id}
                        onClick={() => onDeclinePayment(enquiry)}
                      />
                    </div>
                  ) : (
                    <span
                      className={`flex-shrink-0 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium ${
                        declined ? DECLINED_STYLE : STATUS_STYLES[enquiry.status]
                      }`}
                    >
                      {declined ? "Payment declined" : enquiry.statusLabel}
                    </span>
                  )}
                </div>
              </div>
            </li>
          );
        })}

        {loadingMore && (
          <li className="py-3 text-center text-[13px] text-ink-subtle">
            Loading more enquiries…
          </li>
        )}
      </ul>
    </div>
  );
};

export default EnquiryCards;
